import {
  Avatar,
  AvatarFallback,
  AvatarGroup,
  AvatarImage,
} from "@dialogueconsulting/sked-ui";
import { cn } from "@/lib/utils";
import {
  PlatformIcon,
  getPlatformIconName,
} from "@insights/features/account-avatar/AccountAvatar";

export interface SkedAvatarProps {
  src?: string | null;
  alt?: string;
  size?: "sm" | "md" | "lg" | "small" | "medium" | "large" | number;
  className?: string;
  noBorder?: boolean;
  noBackgroundOnAvatarDisplayed?: boolean;
  platform?: string;
  alert?: boolean;
  color?: string;
}

export interface SkedAvatarGroupAccount {
  id?: string;
  name?: string;
  avatar?: string | null;
  src?: string | null;
  alt?: string;
}

const avatarSizeClass = (size: SkedAvatarProps["size"]) => {
  if (typeof size === "number") {
    if (size <= 24) return "h-6 w-6";
    if (size <= 32) return "h-8 w-8";
    if (size <= 48) return "h-12 w-12";
    return "h-16 w-16";
  }

  if (size === "sm" || size === "small") return "h-6 w-6";
  if (size === "lg" || size === "large") return "h-12 w-12";
  return "h-8 w-8";
};

const normalizeAvatarGroupSize = (
  size: SkedAvatarProps["size"]
): "sm" | "md" | "lg" | undefined => {
  if (typeof size === "number") {
    if (size <= 24) return "sm";
    if (size <= 32) return "md";
    return "lg";
  }

  if (size === "small") return "sm";
  if (size === "medium") return "md";
  if (size === "large") return "lg";
  return size;
};

const avatarPx = (size: SkedAvatarProps["size"]): number => {
  if (typeof size === "number") return size;
  if (size === "sm" || size === "small") return 24;
  if (size === "lg" || size === "large") return 48;
  return 32;
};

const fallbackFor = (alt?: string) =>
  (alt || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

export function SkedAvatar({
  src,
  alt,
  size = "md",
  className,
  noBorder,
  platform,
}: SkedAvatarProps) {
  const avatar = (
    <Avatar
      className={cn(
        avatarSizeClass(size),
        !noBorder && "border border-neutral-200",
        className
      )}
    >
      {src && <AvatarImage src={src} alt={alt || ""} />}
      <AvatarFallback>{fallbackFor(alt)}</AvatarFallback>
    </Avatar>
  );

  if (!platform) return avatar;

  // Overlay the platform account icon (bottom-right) so it's always clear which
  // platform a profile belongs to — sentiment mentions, competitors, etc.
  const wrapperPx = avatarPx(size);
  const badgePx = Math.round(wrapperPx * 0.5);
  return (
    // Size the wrapper to the avatar via inline styles so the badge always
    // anchors to the avatar's corner. Inline width/height beat any ancestor
    // `span { width }` rule (e.g. AccountCell's name-truncation selector),
    // which would otherwise stretch this wrapper and detach the badge.
    <span
      className="relative inline-flex shrink-0"
      style={{ width: wrapperPx, height: wrapperPx }}
    >
      {avatar}
      <PlatformIcon
        aria-hidden
        className={`platform-icon-${getPlatformIconName(platform)}`}
        style={{
          position: "absolute",
          right: -2,
          bottom: -2,
          width: badgePx,
          height: badgePx,
        }}
      />
    </span>
  );
}

export function SkedAvatarGroup({
  accounts,
  size,
  className,
}: {
  accounts: SkedAvatarGroupAccount[];
  size?: SkedAvatarProps["size"];
  className?: string;
}) {
  return (
    <AvatarGroup
      className={className}
      size={normalizeAvatarGroupSize(size)}
      avatars={accounts.map((account) => ({
        src: account.avatar || account.src || undefined,
        alt: account.name || account.alt || "",
      }))}
    />
  );
}
